/**
 * HUD Renderer - IHADSS (Integrated Helmet And Display Sighting System)
 * Renders the green phosphor HUD overlay
 */

import type { PhysicsState } from '../systems/HelicopterPhysics';

export class HUDRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private readonly hudColor = '#00FF40'; // Phosphor green
  private readonly warningColor = '#FFB000'; // Amber

  constructor() {
    this.canvas = document.getElementById('hud-canvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d')!;
    this.resize(window.innerWidth, window.innerHeight);
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  render(
    state: Readonly<PhysicsState>,
    weaponStatus: { currentWeapon: string; ammo: number; maxAmmo: number }
  ): void {
    const { width, height } = this.canvas;
    
    // Clear canvas
    this.ctx.clearRect(0, 0, width, height);

    // Set up drawing context
    this.ctx.strokeStyle = this.hudColor;
    this.ctx.fillStyle = this.hudColor;
    this.ctx.lineWidth = 2;
    this.ctx.font = '16px "Courier New", monospace';

    // Draw heading tape (top center)
    this.drawHeadingTape(width, state.rotation.yaw);

    // Draw altitude (right side)
    this.drawAltitude(width, height, state.position.y);

    // Draw torque indicator (left side)
    this.drawTorque(height, state.torque);

    // Draw weapon crate (bottom right)
    this.drawWeaponCrate(width, height, weaponStatus);

    // Draw crosshair (center)
    this.drawCrosshair(width, height);

    // Draw velocity vector
    this.drawVelocityVector(width, height, state.velocity);
  }

  private drawHeadingTape(width: number, yaw: number): void {
    const centerX = width / 2;
    const y = 50;

    // Convert yaw to degrees (0-360)
    const heading = ((yaw * (180 / Math.PI) + 360) % 360);

    this.ctx.fillStyle = this.hudColor;
    this.ctx.font = '20px "Courier New", monospace';
    this.ctx.textAlign = 'center';
    
    // Draw heading numbers
    for (let i = -2; i <= 2; i++) {
      const displayHeading = Math.floor((heading + i * 30 + 360) % 360);
      const x = centerX + i * 80;
      
      if (i === 0) {
        // Current heading (larger)
        this.ctx.font = '24px "Courier New", monospace';
        this.ctx.fillText(displayHeading.toString().padStart(3, '0'), x, y);
        this.ctx.font = '20px "Courier New", monospace';
      } else {
        this.ctx.fillText(displayHeading.toString().padStart(3, '0'), x, y);
      }
    }

    // Heading tape line
    this.ctx.beginPath();
    this.ctx.moveTo(centerX - 150, y + 10);
    this.ctx.lineTo(centerX + 150, y + 10);
    this.ctx.stroke();

    // Center marker
    this.ctx.beginPath();
    this.ctx.moveTo(centerX, y + 10);
    this.ctx.lineTo(centerX, y + 20);
    this.ctx.stroke();
  }

  private drawAltitude(width: number, height: number, altitude: number): void {
    const x = width - 100;
    const y = height / 2;

    this.ctx.fillStyle = this.hudColor;
    this.ctx.font = '20px "Courier New", monospace';
    this.ctx.textAlign = 'right';

    // Label
    this.ctx.fillText('ALT', x, y - 60);

    // Altitude value (in meters, convert to feet for display)
    const altitudeFeet = Math.floor(altitude * 3.28084);
    this.ctx.font = '24px "Courier New", monospace';
    this.ctx.fillText(altitudeFeet.toString(), x, y);

    // AGL indicator
    this.ctx.font = '16px "Courier New", monospace';
    this.ctx.fillText('AGL', x, y + 25);

    // Altitude ladder
    this.ctx.beginPath();
    for (let i = -3; i <= 3; i++) {
      const tickY = y + i * 30;
      this.ctx.moveTo(x - 40, tickY);
      this.ctx.lineTo(x - 20, tickY);
    }
    this.ctx.stroke();
  }

  private drawTorque(height: number, torque: number): void {
    const x = 80;
    const y = height / 2;
    const barHeight = 200;

    this.ctx.fillStyle = this.hudColor;
    this.ctx.font = '16px "Courier New", monospace';
    this.ctx.textAlign = 'left';

    // Label
    this.ctx.fillText('TRQ', x - 20, y - barHeight / 2 - 20);

    // Torque bar background
    this.ctx.strokeStyle = this.hudColor;
    this.ctx.strokeRect(x, y - barHeight / 2, 20, barHeight);

    // Torque fill
    const fillHeight = (torque / 110) * barHeight;
    const fillColor = torque > 100 ? this.warningColor : this.hudColor;
    this.ctx.fillStyle = fillColor;
    this.ctx.fillRect(x + 2, y + barHeight / 2 - fillHeight, 16, fillHeight);

    // Torque value
    this.ctx.fillStyle = this.hudColor;
    this.ctx.fillText(Math.floor(torque) + '%', x - 5, y + barHeight / 2 + 25);
  }

  private drawWeaponCrate(
    width: number,
    height: number,
    weaponStatus: { currentWeapon: string; ammo: number; maxAmmo: number }
  ): void {
    const x = width - 200;
    const y = height - 100;
    const boxWidth = 180;
    const boxHeight = 80;

    // Dashed box
    this.ctx.setLineDash([5, 5]);
    this.ctx.strokeStyle = this.hudColor;
    this.ctx.strokeRect(x, y, boxWidth, boxHeight);
    this.ctx.setLineDash([]);

    // Weapon name
    this.ctx.fillStyle = this.hudColor;
    this.ctx.font = '18px "Courier New", monospace';
    this.ctx.textAlign = 'left';
    this.ctx.fillText(weaponStatus.currentWeapon, x + 10, y + 25);

    // Ammo count
    this.ctx.font = '24px "Courier New", monospace';
    const ammoText = `${weaponStatus.ammo}/${weaponStatus.maxAmmo}`;
    this.ctx.fillText(ammoText, x + 10, y + 55);
  }

  private drawCrosshair(width: number, height: number): void {
    const centerX = width / 2;
    const centerY = height / 2;
    const size = 20;

    this.ctx.strokeStyle = this.hudColor;
    this.ctx.lineWidth = 2;

    // I-beam cursor
    this.ctx.beginPath();
    
    // Vertical line
    this.ctx.moveTo(centerX, centerY - size);
    this.ctx.lineTo(centerX, centerY + size);
    
    // Top horizontal
    this.ctx.moveTo(centerX - size / 2, centerY - size);
    this.ctx.lineTo(centerX + size / 2, centerY - size);
    
    // Bottom horizontal
    this.ctx.moveTo(centerX - size / 2, centerY + size);
    this.ctx.lineTo(centerX + size / 2, centerY + size);
    
    this.ctx.stroke();
  }

  private drawVelocityVector(
    width: number,
    height: number,
    velocity: { x: number; y: number; z: number }
  ): void {
    const centerX = width / 2;
    const centerY = height / 2;

    // Calculate velocity magnitude
    const speed = Math.sqrt(velocity.x ** 2 + velocity.y ** 2 + velocity.z ** 2);
    
    if (speed < 1) return; // Don't draw if stationary

    // Simple velocity vector indicator (circle with wings)
    const offsetX = velocity.x * 2;
    const offsetY = -velocity.y * 2; // Invert Y for screen coordinates

    const x = centerX + offsetX;
    const y = centerY + offsetY;

    this.ctx.strokeStyle = this.hudColor;
    this.ctx.lineWidth = 2;

    // Circle
    this.ctx.beginPath();
    this.ctx.arc(x, y, 10, 0, Math.PI * 2);
    this.ctx.stroke();

    // Wings
    this.ctx.beginPath();
    this.ctx.moveTo(x - 15, y);
    this.ctx.lineTo(x - 10, y);
    this.ctx.moveTo(x + 10, y);
    this.ctx.lineTo(x + 15, y);
    this.ctx.stroke();
  }
}
